import { isEan13, isUpcA, toEan13FromUpcA, validateEan13Checksum } from '@stalvi/contracts';

export type ScannerCallbacks = {
  onValid: (ean13: string) => void;
  onInvalid: (reason: 'checksum' | 'unsupported') => void;
  onError?: (err: Error) => void;
};

export type ScannerOpts = {
  videoEl: HTMLVideoElement;
  stream?: MediaStream;
} & ScannerCallbacks;

interface BarcodeDetectorResult {
  rawValue: string;
  format: string;
}

interface BarcodeDetectorInstance {
  detect: (src: unknown) => Promise<BarcodeDetectorResult[]>;
}

interface BarcodeDetectorCtor {
  new (opts?: { formats?: string[] }): BarcodeDetectorInstance;
  getSupportedFormats?: () => Promise<readonly string[]>;
}

const POLYFILL_TICK_MS = 150;
const SUPPORTED_FORMATS: ReadonlyArray<'ean_13' | 'upc_a'> = ['ean_13', 'upc_a'];

/**
 * Region-of-interest geometry — a centered band of the camera frame, expressed
 * as fractions of the source dimensions. The decoder is fed *only* this crop
 * (issue #161, primary decode lever for small/curved/distant codes): cutting
 * the surrounding clutter lets ZXing-C++ lock onto the bars instead of scanning
 * a mush of the whole frame.
 *
 * These two constants are the single source of truth for the crop AND for the
 * on-screen reticle (BarcodeScanner draws its framing rectangle from the same
 * fractions), so the visible aim box and the decoded region never drift. With
 * `object-cover` video the two align exactly only when the frame and the
 * viewfinder share an aspect ratio; otherwise they match approximately — which
 * is the intended "roughly matching" behaviour for this stopgap.
 */
export const ROI_WIDTH_FRAC = 0.8;
export const ROI_HEIGHT_FRAC = 0.4;

interface RoiRect {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
}

/**
 * Centered ROI rectangle in source-pixel coordinates, or `null` when the video
 * has not produced a frame yet (`videoWidth`/`videoHeight` still 0).
 */
function computeRoi(videoEl: HTMLVideoElement): RoiRect | null {
  const vw = videoEl.videoWidth;
  const vh = videoEl.videoHeight;
  if (vw <= 0 || vh <= 0) return null;
  const sw = Math.round(vw * ROI_WIDTH_FRAC);
  const sh = Math.round(vh * ROI_HEIGHT_FRAC);
  const sx = Math.round((vw - sw) / 2);
  const sy = Math.round((vh - sh) / 2);
  return { sx, sy, sw, sh };
}

function getNativeDetector(): BarcodeDetectorCtor | undefined {
  return (globalThis as unknown as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector;
}

/**
 * True when the platform ships a native `BarcodeDetector` that actually lists
 * `ean_13`/`upc_a` in `getSupportedFormats()`. This is the capability gate that
 * routes the scanner (issue #167): Chrome/Chromium answer `true` (keep the live
 * viewfinder loop); iOS Safari and any WebKit/iOS browser answer `false` —
 * either the API is absent or it lies in `getSupportedFormats()` / throws — and
 * take the tap-to-capture single-still fallback instead of the memory-hungry
 * polyfill live loop that crosses WebKit's Jetsam ceiling. Same probe
 * `resolveBarcodeDetector` runs; never UA sniffing.
 */
export async function hasHealthyNativeDetector(): Promise<boolean> {
  const native = getNativeDetector();
  if (typeof native !== 'function') return false;
  try {
    const supported = (await native.getSupportedFormats?.()) ?? [];
    return supported.includes('ean_13') || supported.includes('upc_a');
  } catch {
    return false;
  }
}

/**
 * `locateFile` override for `zxing-wasm`. Our CSP only allows 'self' for
 * scripts/connect-src, so the polyfill's default jsDelivr CDN is blocked. The
 * .wasm file ships as a static asset under /wasm/zxing_reader.wasm.
 */
function zxingLocateFile(filename: string, prefix: string): string {
  return filename.endsWith('.wasm') ? `/wasm/${filename}` : prefix + filename;
}

async function loadPolyfillCtor(): Promise<BarcodeDetectorCtor> {
  const mod = await import('barcode-detector/pure');
  mod.setZXingModuleOverrides({ locateFile: zxingLocateFile });
  return mod.BarcodeDetector as unknown as BarcodeDetectorCtor;
}

/**
 * Resolve a BarcodeDetector implementation that actually decodes EAN_13 / UPC_A.
 * Tries the native API first (zero overhead on Chrome). Falls back to the
 * `barcode-detector/pure` polyfill (WASM ZXing-C++) when the native impl is
 * missing or partial — the latter is the case on Safari macOS / iOS where the
 * native BarcodeDetector exists but does not list ean_13 in supported formats,
 * which silently yields zero detections forever. The polyfill exposes the same
 * BarcodeDetectorOptions / detect() surface, so the tick loop is identical.
 */
async function resolveBarcodeDetector(): Promise<BarcodeDetectorCtor> {
  if (await hasHealthyNativeDetector()) return getNativeDetector() as BarcodeDetectorCtor;
  return loadPolyfillCtor();
}

/**
 * Initialize the detector instance. Returns the constructor + instance, or
 * `null` if initialization failed (error already forwarded to onError).
 */
async function initDetector(opts: ScannerOpts): Promise<{
  Detector: BarcodeDetectorCtor;
  detector: BarcodeDetectorInstance;
  usingNative: boolean;
} | null> {
  let Detector: BarcodeDetectorCtor;
  try {
    Detector = await resolveBarcodeDetector();
  } catch (e) {
    opts.onError?.(e as Error);
    return null;
  }
  const usingNative = Detector === getNativeDetector();
  let detector: BarcodeDetectorInstance;
  try {
    detector = new Detector({ formats: [...SUPPORTED_FORMATS] });
  } catch (e) {
    opts.onError?.(e as Error);
    return null;
  }
  console.info('[barcode] decoder ready', { path: usingNative ? 'native' : 'polyfill' });
  return { Detector, detector, usingNative };
}

/**
 * Per-frame source feeding `detect()` the centered ROI crop. The same canvas
 * backs both paths:
 *
 *   - `getCanvas()` — for the native detector: draw the ROI band and hand the
 *     <canvas> straight to `detect()` (no `getImageData` readback cost).
 *   - `getImageData()` — for the polyfill: extract `ImageData` from the same
 *     crop so the polyfill skips its OffscreenCanvas / createImageBitmap branch
 *     (unreliable on WebKit). `willReadFrequently: true` forces a CPU-backed
 *     buffer, avoiding the transparent/black readback seen under iOS 10-bit HDR
 *     camera output.
 *
 * Both return `null` until the video has a frame, so callers fall back to the
 * raw <video>. The canvas is sized to the crop, not the full frame, so the
 * decoder only ever sees the reticle band (issue #161).
 */
function createCroppedFrameSource(videoEl: HTMLVideoElement): {
  getCanvas: () => HTMLCanvasElement | null;
  getImageData: () => ImageData | null;
} {
  let canvas: HTMLCanvasElement | null = null;
  let ctx: CanvasRenderingContext2D | null = null;

  function drawCrop(): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } | null {
    const roi = computeRoi(videoEl);
    if (!roi) return null;
    if (!canvas) {
      canvas = document.createElement('canvas');
      ctx = canvas.getContext('2d', { willReadFrequently: true });
    }
    if (!ctx) return null;
    if (canvas.width !== roi.sw) canvas.width = roi.sw;
    if (canvas.height !== roi.sh) canvas.height = roi.sh;
    // Source ROI rect → full destination canvas (1:1, no scaling).
    ctx.drawImage(videoEl, roi.sx, roi.sy, roi.sw, roi.sh, 0, 0, roi.sw, roi.sh);
    return { canvas, ctx };
  }

  return {
    getCanvas(): HTMLCanvasElement | null {
      return drawCrop()?.canvas ?? null;
    },
    getImageData(): ImageData | null {
      const drawn = drawCrop();
      if (!drawn) return null;
      try {
        return drawn.ctx.getImageData(0, 0, drawn.canvas.width, drawn.canvas.height);
      } catch {
        return null;
      }
    },
  };
}

type Classification = { ean13: string } | { reason: 'checksum' } | null;

/**
 * Pure format/checksum gate over one raw decoder result, shared by the live
 * loop (`handleDetection`) and the still fallback (`decodeImageFile`). EAN_13
 * is taken as-is; UPC_A is zero-padded up to EAN_13; anything else returns
 * `null` (ignored — not our format). A matched value that fails Mod-10 returns
 * `{ reason: 'checksum' }`.
 */
function classifyDetection(rawValue: string, format: string): Classification {
  let ean13: string | null = null;
  if (format === 'upc_a' && isUpcA(rawValue)) {
    ean13 = toEan13FromUpcA(rawValue);
  } else if (format === 'ean_13' && isEan13(rawValue)) {
    ean13 = rawValue;
  } else {
    return null;
  }
  return validateEan13Checksum(ean13) ? { ean13 } : { reason: 'checksum' };
}

/**
 * Map a single raw decoder result to the appropriate scanner callback. EAN_13
 * is forwarded as-is; UPC_A is up-converted to EAN_13. Anything that fails the
 * format / checksum gates is rejected via `onInvalid('checksum')`.
 */
function handleDetection(rawValue: string, format: string, callbacks: ScannerCallbacks): void {
  const c = classifyDetection(rawValue, format);
  if (!c) return;
  if ('ean13' in c) callbacks.onValid(c.ean13);
  else callbacks.onInvalid('checksum');
}

export type StillDecodeResult = { ean13: string } | { reason: 'checksum' | 'not-found' };

/**
 * Decode the captured photo into an `ImageBitmap`, honouring its EXIF
 * orientation. iOS camera stills are commonly stored rotated with an EXIF
 * orientation tag; without `imageOrientation: 'from-image'` the bitmap is
 * un-rotated and a 90°/270° barcode never decodes on the 1D scanner. Falls back
 * to the single-arg form for engines that reject the options overload.
 */
async function loadOrientedBitmap(file: Blob): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return createImageBitmap(file);
  }
}

/**
 * Extract `ImageData` from the bitmap via a `willReadFrequently` (CPU-backed)
 * canvas — the same readback the live polyfill path uses (see
 * `createCroppedFrameSource`). On WebKit, handing the raw bitmap to `detect()`
 * routes through the polyfill's OffscreenCanvas / createImageBitmap branch,
 * which is unreliable on iOS (transparent/black readback under HDR-capable
 * camera output) — exactly the platform this still path targets. Pre-extracting
 * ImageData sidesteps it. Returns `null` when no 2D context is available so the
 * caller can fall back to the bitmap.
 */
function bitmapToImageData(bitmap: ImageBitmap): ImageData | null {
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(bitmap, 0, 0);
  try {
    return ctx.getImageData(0, 0, canvas.width, canvas.height);
  } catch {
    return null;
  }
}

/**
 * One-shot still-image decode for the iOS Safari/WebKit tap-to-capture fallback
 * (issue #167). Loads the captured photo (EXIF-oriented), extracts ImageData via
 * a CPU-backed canvas, and runs the self-hosted `zxing-wasm` polyfill `detect()`
 * exactly **once** — no `getUserMedia`, no `<video>`, no tick loop — so the WASM
 * heap never grows unbounded and the tab survives. The full-resolution,
 * OS-autofocused still also decodes small/curved/distant codes better than
 * downscaled live frames. No ROI crop: the whole image is decoded.
 *
 * Returns the EAN13 on success, `{ reason: 'checksum' }` when a code matched but
 * failed Mod-10, or `{ reason: 'not-found' }` when nothing decoded (prompts a
 * re-capture). Decoder/load failures reject — the caller surfaces them.
 */
export async function decodeImageFile(file: Blob): Promise<StillDecodeResult> {
  const Polyfill = await loadPolyfillCtor();
  const detector = new Polyfill({ formats: [...SUPPORTED_FORMATS] });
  const bitmap = await loadOrientedBitmap(file);
  try {
    const results = await detector.detect(bitmapToImageData(bitmap) ?? bitmap);
    let sawChecksum = false;
    for (const r of results) {
      const c = classifyDetection(r.rawValue, r.format);
      if (c && 'ean13' in c) return { ean13: c.ean13 };
      if (c) sawChecksum = true;
    }
    return sawChecksum ? { reason: 'checksum' } : { reason: 'not-found' };
  } finally {
    (bitmap as { close?: () => void }).close?.();
  }
}

export function createScanner(opts: ScannerOpts): { stop: () => void } {
  let stopped = false;
  let rafId: number | null = null;
  let timerId: ReturnType<typeof setTimeout> | null = null;

  function teardown(): void {
    stopped = true;
    if (rafId !== null) {
      if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(rafId);
      rafId = null;
    }
    if (timerId !== null) {
      clearTimeout(timerId);
      timerId = null;
    }
  }

  void runScanLoop(opts, () => stopped, {
    setRafId: (id) => {
      rafId = id;
    },
    setTimerId: (id) => {
      timerId = id;
    },
  });

  return { stop: teardown };
}

interface LoopHandles {
  setRafId: (id: number | null) => void;
  setTimerId: (id: ReturnType<typeof setTimeout> | null) => void;
}

/**
 * Mutable per-run state shared across loop helpers. Held in a single object
 * (rather than free `let` bindings) so that helpers extracted to module scope
 * can mutate the same `detector` / `usingNative` slots that the live `tick`
 * closure reads — preserving the hot-swap semantics of the original inline
 * loop bit-for-bit.
 */
interface LoopState {
  detector: BarcodeDetectorInstance;
  usingNative: boolean;
}

/**
 * Immutable per-run context. Bundles the dependencies every loop helper needs
 * so each helper signature stays narrow.
 */
interface LoopContext {
  opts: ScannerOpts;
  isStopped: () => boolean;
  handles: LoopHandles;
  frame: {
    getCanvas: () => HTMLCanvasElement | null;
    getImageData: () => ImageData | null;
  };
}

/**
 * Hot-swap from native to polyfill at runtime. See `handleTickError` for the
 * trigger condition. Returns true if the swap succeeded.
 */
async function swapToPolyfill(state: LoopState, ctx: LoopContext): Promise<boolean> {
  if (!state.usingNative) return false;
  try {
    const Polyfill = await loadPolyfillCtor();
    if (ctx.isStopped()) return false;
    state.detector = new Polyfill({ formats: [...SUPPORTED_FORMATS] });
    state.usingNative = false;
    console.info('[barcode] swapped native → polyfill after detect failure');
    return true;
  } catch (e) {
    ctx.opts.onError?.(e as Error);
    return false;
  }
}

/**
 * Pick the per-frame source for `detect()`. Both paths decode the centered ROI
 * crop (issue #161), differing only in the surface `detect()` accepts cheaply:
 *
 *   - Native (Chrome): hand the cropped <canvas> — GPU-friendly, no readback.
 *   - Polyfill (Safari): hand pre-extracted ImageData from the same crop so the
 *     polyfill skips its OffscreenCanvas / createImageBitmap branch (unreliable
 *     on WebKit); see `createCroppedFrameSource` for the HDR/willReadFrequently
 *     rationale.
 *
 * Both fall back to the raw <video> when the crop canvas isn't ready yet (e.g.
 * videoWidth still 0), preserving the original first-frames behaviour.
 */
function pickDetectSource(state: LoopState, ctx: LoopContext): unknown {
  if (state.usingNative) return ctx.frame.getCanvas() ?? ctx.opts.videoEl;
  return ctx.frame.getImageData() ?? ctx.opts.videoEl;
}

function scheduleNextTick(state: LoopState, ctx: LoopContext): void {
  const runTick = (): void => {
    void tick(state, ctx);
  };
  if (state.usingNative && typeof requestAnimationFrame === 'function') {
    const id = requestAnimationFrame(runTick);
    ctx.handles.setRafId(id);
  } else {
    const id = setTimeout(runTick, POLYFILL_TICK_MS);
    ctx.handles.setTimerId(id);
  }
}

async function handleTickError(e: unknown, state: LoopState, ctx: LoopContext): Promise<boolean> {
  // Safari ships a BarcodeDetector that lies in getSupportedFormats() and
  // throws on every detect(). Rather than try to recognise which specific
  // error shape WebKit chose this release, swap on **any** error from the
  // native detector. The polyfill is the safer default when the native one
  // shows ANY sign of being unhealthy. This costs one wasted tick + one
  // WASM lazy-load on Safari and is a no-op in Chrome.
  console.warn('[barcode] native detect threw', {
    name: (e as Error)?.name,
    message: (e as Error)?.message,
    usingNative: state.usingNative,
  });
  if (state.usingNative) {
    const swapped = await swapToPolyfill(state, ctx);
    if (!swapped) {
      ctx.opts.onError?.(e as Error);
      return false;
    }
    return true;
  }
  ctx.opts.onError?.(e as Error);
  return true;
}

/**
 * Run the detector once and dispatch any detections. Returns true if the loop
 * should continue scheduling, false if a fatal error halted it.
 */
async function runDetectAndDispatch(state: LoopState, ctx: LoopContext): Promise<boolean> {
  try {
    const results = await state.detector.detect(pickDetectSource(state, ctx));
    if (ctx.isStopped()) return false;
    for (const r of results) {
      handleDetection(r.rawValue, r.format, ctx.opts);
      if (ctx.isStopped()) return false;
    }
    return true;
  } catch (e) {
    return handleTickError(e, state, ctx);
  }
}

async function tick(state: LoopState, ctx: LoopContext): Promise<void> {
  if (ctx.isStopped()) return;
  const continueLoop = await runDetectAndDispatch(state, ctx);
  if (!continueLoop) return;
  if (ctx.isStopped()) return;
  scheduleNextTick(state, ctx);
}

/**
 * Main detect → schedule loop. Bootstraps the detector, then ticks
 * indefinitely (raf for native, setTimeout for polyfill) until `isStopped()`
 * flips to true. Hot-swaps native → polyfill on the first detect() error
 * because Safari ships a BarcodeDetector that lies in getSupportedFormats()
 * and throws on every detect() call.
 */
async function runScanLoop(opts: ScannerOpts, isStopped: () => boolean, handles: LoopHandles): Promise<void> {
  const init = await initDetector(opts);
  if (!init || isStopped()) return;
  const state: LoopState = { detector: init.detector, usingNative: init.usingNative };
  const ctx: LoopContext = {
    opts,
    isStopped,
    handles,
    frame: createCroppedFrameSource(opts.videoEl),
  };
  void tick(state, ctx);
}
